import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Pagination, EffectFade, Zoom } from 'swiper/modules';
import { Heart, Share2, ShoppingCart, Plus, Minus, Package2, Shield, Truck, X, AlertCircle, Tag, Gift } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'react-hot-toast';
import { Product, SelectedVariation } from '@/types';
import { useStore } from '@/store';
import { useStoreInfo } from '@/lib/store';
import { formatPrice, getDomain, truncateText, hasDiscount, calculateDiscount, getDiscountedPrice } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { LockNotice } from '@/components/site-lock/lock-notice';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import 'swiper/css/effect-fade';
import 'swiper/css/zoom';

const features = [
  {
    icon: Truck,
    title: 'Fast Delivery',
    description: 'Get your order delivered quickly and securely'
  },
  {
    icon: Shield,
    title: 'Secure Payment',
    description: 'Your payment information is always protected'
  },
  {
    icon: Package2,
    title: 'Quality Guarantee',
    description: '100% satisfaction guaranteed on all products'
  }
];

export function ProductPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [product, setProduct] = useState<Product | null>(null);
  const [suggestedProducts, setSuggestedProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedVariations, setSelectedVariations] = useState<SelectedVariation[]>([]);
  const [quantity, setQuantity] = useState(1);
  const [selectedImage, setSelectedImage] = useState(0);
  const [showAddedToCartModal, setShowAddedToCartModal] = useState(false);
  const [showLockNotice, setShowLockNotice] = useState(false);

  const { addToCart, toggleWishlist, wishlist } = useStore();
  const { storeInfo } = useStoreInfo();
  const isWishlisted = wishlist.includes(parseInt(id!));

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const domain = getDomain();
        const response = await fetch(`${import.meta.env.VITE_API_ENDPOINT}/products.php?domain=${domain}`);
        if (!response.ok) throw new Error('Failed to fetch products');

        const data = await response.json();
        if (!data.status) throw new Error(data.message);

        const foundProduct = data.products.find((p: Product) => p.id === parseInt(id!));
        if (!foundProduct) {
          setError('Product not found');
          setLoading(false);
          return;
        }
        
        if (foundProduct.status === 3) {
          setError('Product is not available');
          setLoading(false);
          return;
        }

        setProduct(foundProduct);

        const categoryProducts = data.products.filter((p: Product) =>
          p.category === foundProduct.category &&
          p.id !== foundProduct.id &&
          p.status === 1
        );

        const shuffled = categoryProducts.sort(() => 0.5 - Math.random());
        setSuggestedProducts(shuffled.slice(0, 4));

        if (foundProduct.variations.length > 0 && foundProduct.variations[0].variation !== 'Nil') {
          setSelectedVariations(foundProduct.variations.map(v => ({
            v_id: v.v_id,
            variation: v.variation,
            quantity: 0,
            variation_amount: v.variation_amount
          })));
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load product');
      } finally {
        setLoading(false);
      }
    };

    setLoading(true);
    fetchProducts();
  }, [id]);

  useEffect(() => {
    if (storeInfo?.site_lock === 1) {
      setShowLockNotice(true);
    }
  }, [storeInfo]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 dark:bg-gray-900 py-8">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg overflow-hidden">
            <div className="md:grid md:grid-cols-2 md:gap-8">
              <div className="animate-pulse">
                <div className="aspect-square bg-gray-200 dark:bg-gray-700" />
                <div className="p-4 flex gap-2">
                  {[...Array(4)].map((_, i) => (
                    <div key={i} className="w-16 h-16 bg-gray-200 dark:bg-gray-700 rounded-lg" />
                  ))}
                </div>
              </div>
              <div className="p-6 space-y-4">
                <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded w-3/4" />
                <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-1/4" />
                <div className="h-6 bg-gray-200 dark:bg-gray-700 rounded w-1/3 mt-6" />
                <div className="space-y-2 mt-8">
                  <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-full" />
                  <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-full" />
                  <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-3/4" />
                </div>
                <div className="h-12 bg-gray-200 dark:bg-gray-700 rounded mt-8" />
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error || !product) {
    return (
      <div className="min-h-screen bg-gray-100 dark:bg-gray-900 py-12">
        <div className="max-w-md mx-auto px-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6 text-center">
            <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
              Product Not Found
            </h2>
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              {error || 'The product you are looking for does not exist or has been removed.'}
            </p>
            <div className="flex justify-center gap-4">
              <Button
                variant="outline"
                onClick={() => window.history.back()}
              >
                Go Back
              </Button>
              <Link to="/products">
                <Button>
                  Browse Products
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const handleVariationQuantityChange = (variation: string, change: number) => {
    setSelectedVariations(prev => prev.map(v => {
      if (v.variation === variation) {
        const currentStock = product?.variations.find(pv => pv.variation === variation)?.v_qty || '0';
        const newQuantity = Math.max(0, Math.min(parseInt(currentStock), v.quantity + change));
        return { ...v, quantity: newQuantity };
      }
      return v;
    }));
  };

  const handleAddToCart = () => {
    if (storeInfo?.site_lock === 1) {
      setShowLockNotice(true);
      return;
    }

    if (!product) return;

    const isOutOfStock = product.variations[0].variation !== 'Nil'
      ? product.variations.every(v => parseInt(v.v_qty) === 0)
      : parseInt(product.totalStock.toString()) === 0;

    if (isOutOfStock) {
      toast.error('This product is out of stock');
      return;
    }

      if (product.variations[0].variation !== 'Nil') {
      const totalSelected = selectedVariations.reduce((sum, v) => sum + v.quantity, 0);
      if (totalSelected === 0) {
        toast.error('Please select at least one variation');
        return;
      }

      addToCart({
        ...product,
        quantity: 1,
          selectedVariations: selectedVariations
            .filter(v => v.quantity > 0)
            .map(v => {
              const canonical = product.variations.find(pv => pv.variation === v.variation);
              return {
                v_id: canonical ? canonical.v_id : (v.v_id || ''),
                variation: v.variation,
                quantity: v.quantity,
                variation_amount: canonical ? canonical.variation_amount : (v.variation_amount || '')
              };
            }),
      });
    } else {
      if (quantity > parseInt(product.totalStock.toString())) {
        toast.error('Selected quantity exceeds available stock');
        return;
      }
      addToCart({
        ...product,
        quantity,
        selectedVariations: undefined,
      });
    }
    setShowAddedToCartModal(true);
  };

  const handleWishlist = () => {
    if (!product) return;
    toggleWishlist(product.id);
    toast.success(isWishlisted ? 'Removed from wishlist' : 'Added to wishlist');
  };

  const handleShare = async () => {
    try {
      const shareData = {
        title: product?.produt_name,
        text: product?.description,
        url: window.location.href,
      };

      const shareLinks = {
        whatsapp: `https://wa.me/?text=${encodeURIComponent(`${shareData.title}\n\n${shareData.url}`)}`,
        telegram: `https://t.me/share/url?url=${encodeURIComponent(shareData.url)}&text=${encodeURIComponent(shareData.title)}`,
        twitter: `https://twitter.com/intent/tweet?text=${encodeURIComponent(`${shareData.title}\n\n${shareData.url}`)}`,
        facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareData.url)}`,
      };

      const shareDialog = document.createElement('div');
      shareDialog.className = 'fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50';
      shareDialog.innerHTML = `
        <div class="bg-white dark:bg-gray-800 rounded-lg shadow-xl p-6 max-w-sm w-full">
          <h3 class="text-lg font-medium text-gray-900 dark:text-white mb-4">Share Product</h3>
          <div class="grid grid-cols-2 gap-4">
            <a href="${shareLinks.whatsapp}" target="_blank" rel="noopener noreferrer" 
               class="flex items-center justify-center gap-2 p-3 rounded-lg bg-green-500 text-white hover:bg-green-600">
              WhatsApp
            </a>
            <a href="${shareLinks.telegram}" target="_blank" rel="noopener noreferrer"
               class="flex items-center justify-center gap-2 p-3 rounded-lg bg-brand-primary text-white hover:bg-brand-light">
              Telegram
            </a>
            <a href="${shareLinks.twitter}" target="_blank" rel="noopener noreferrer"
               class="flex items-center justify-center gap-2 p-3 rounded-lg bg-brand-primary text-white hover:bg-brand-light">
              Twitter
            </a>
            <a href="${shareLinks.facebook}" target="_blank" rel="noopener noreferrer"
               class="flex items-center justify-center gap-2 p-3 rounded-lg bg-brand-primary text-white hover:bg-brand-light">
              Facebook
            </a>
          </div>
          <button class="mt-4 w-full p-2 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white">
            Cancel
          </button>
        </div>
      `;

      document.body.appendChild(shareDialog);

      shareDialog.addEventListener('click', (e) => {
        if (e.target === shareDialog || (e.target as HTMLElement).tagName === 'BUTTON') {
          shareDialog.remove();
        }
      });

    } catch (err) {
      toast.error('Failed to share product');
    }
  };

  const AddedToCartModal = () => (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl p-6 max-w-sm w-full">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-medium text-gray-900 dark:text-white">Added to Cart</h3>
          <button
            onClick={() => setShowAddedToCartModal(false)}
            className="text-gray-400 hover:text-gray-500 dark:hover:text-gray-300"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
        <p className="text-gray-600 dark:text-gray-300 mb-6">
          {product?.produt_name} has been added to your cart.
        </p>
        <div className="flex gap-4">
          <Button
            variant="outline"
            className="flex-1"
            onClick={() => {
              setShowAddedToCartModal(false);
              navigate('/products');
            }}
          >
            Keep Shopping
          </Button>
          <Button
            className="flex-1"
            onClick={() => {
              setShowAddedToCartModal(false);
              navigate('/cart');
            }}
          >
            Checkout
          </Button>
        </div>
      </div>
    </div>
  );

  const availableVariations = product?.variations.filter(v => parseInt(v.v_qty) > 0) || [];

  // Calculate total quantity for discount eligibility
  const totalQuantity = product?.variations[0].variation !== 'Nil'
    ? selectedVariations.reduce((sum, v) => sum + v.quantity, 0)
    : quantity;

  const discountInfo = product ? calculateDiscount(product, totalQuantity) : null;
  const finalPrice = product ? getDiscountedPrice(product, totalQuantity) : 0;

  return (
    <>
      <Helmet>
        <title>{product.produt_name} - {storeInfo?.site_name || 'Store'}</title>
        <meta name="description" content={product.description} />
        <meta property="og:title" content={product.produt_name} />
        <meta property="og:description" content={product.description} />
        <meta property="og:image" content={product.images[0]} />
        <meta property="og:url" content={window.location.href} />
      </Helmet>

      <div className="min-h-screen bg-gray-100 dark:bg-gray-900 py-8">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg overflow-hidden">
            <div className="md:grid md:grid-cols-2 md:gap-8">
              <div className="product-gallery">
                <Swiper
                  modules={[Navigation, Pagination, EffectFade, Zoom]}
                  navigation
                  pagination={{ clickable: true }}
                  effect="fade"
                  zoom={true}
                  className="aspect-square"
                  onSlideChange={(swiper) => setSelectedImage(swiper.activeIndex)}
                >
                  {product.images.map((image, index) => (
                    <SwiperSlide key={index}>
                      <div className="swiper-zoom-container">
                        <img
                          src={image}
                          alt={`${product.produt_name} - Image ${index + 1}`}
                          className="w-full h-full object-cover"
                        />
                        {hasDiscount(product) && (
                          <div className="absolute top-4 left-4 bg-red-500 text-white px-3 py-2 rounded-full text-sm font-semibold flex items-center gap-2 z-10">
                            <Tag className="h-4 w-4" />
                            {product.discount_type === 'percentage' 
                              ? `${product.discount_off}% OFF`
                              : `${formatPrice(parseFloat(String(product.discount_off) || '0'), storeInfo?.currenCy)} OFF`}
                          </div>
                        )}
                      </div>
                    </SwiperSlide>
                  ))}
                </Swiper>
                <div className="product-thumbnails">
                  <div className="product-thumbnails-container">
                    {product.images.map((image, index) => (
                      <button
                        key={index}
                        onClick={() => setSelectedImage(index)}
                        className={`product-thumbnail ${
                          selectedImage === index
                            ? 'border-brand-primary dark:border-brand-light'
                            : 'border-transparent'
                        }`}
                      >
                        <img
                          src={image}
                          alt={`Thumbnail ${index + 1}`}
                          className="w-full h-full object-cover"
                        />
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              <div className="p-6">
                <div className="flex justify-between items-start">
                  <div>
                    <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
                      {product.produt_name}
                    </h1>
                    {product.category && (
                      <Link
                        to={`/products?category=${encodeURIComponent(product.category)}`}
                        className="text-sm text-brand-primary dark:text-brand-light hover:underline"
                      >
                        {product.category}
                      </Link>
                    )}
                  </div>
                  <div className="flex gap-2">
                    <motion.button
                      whileTap={{ scale: 0.95 }}
                      onClick={handleWishlist}
                      className={`p-2 rounded-full ${isWishlisted
                        ? 'bg-red-50 dark:bg-red-900/20 text-red-500 dark:text-red-400'
                        : 'bg-gray-50 dark:bg-gray-700 text-gray-500 dark:text-gray-400'
                        }`}
                    >
                      <Heart className="h-5 w-5" fill={isWishlisted ? 'currentColor' : 'none'} />
                    </motion.button>
                    <motion.button
                      whileTap={{ scale: 0.95 }}
                      onClick={handleShare}
                      className="p-2 rounded-full bg-gray-50 dark:bg-gray-700 text-gray-500 dark:text-gray-400"
                    >
                      <Share2 className="h-5 w-5" />
                    </motion.button>
                  </div>
                </div>

                <div className="mt-6">
                  <div className="flex items-baseline gap-4">
                    <div className="flex flex-col">
                      <span className="text-3xl font-bold text-gray-900 dark:text-white">
                        {discountInfo?.isEligible 
                          ? formatPrice(finalPrice, storeInfo?.currenCy)
                          : formatPrice(parseFloat(product.sellingprice || '0'), storeInfo?.currenCy)}
                      </span>
                      {discountInfo?.isEligible && (
                        <span className="text-lg text-gray-500 dark:text-gray-400 line-through">
                          {formatPrice(parseFloat(product.sellingprice || '0') * totalQuantity, storeInfo?.currenCy)}
                        </span>
                      )}
                    </div>
                    {product.postType === "1" && (
                      <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-yellow-100 dark:bg-yellow-900/20 text-yellow-800 dark:text-yellow-400">
                        Pre-Order
                      </span>
                    )}
                  </div>
                  
                  {/* Discount Information */}
                  {hasDiscount(product) && (
                    <div className="mt-4 p-4 bg-gradient-to-r from-red-50 to-pink-50 dark:from-red-900/20 dark:to-pink-900/20 rounded-lg border border-red-200 dark:border-red-800">
                      <div className="flex items-center gap-2 mb-2">
                        <Gift className="h-5 w-5 text-red-600 dark:text-red-400" />
                        <span className="font-semibold text-red-600 dark:text-red-400">
                          Special Offer
                        </span>
                      </div>
                      <p className="text-sm text-gray-700 dark:text-gray-300">
                        Buy {product.discount_qty} or more items and get{' '}
                        <span className="font-bold text-red-600 dark:text-red-400">
                          {product.discount_type === 'percentage' 
                            ? `${product.discount_off}% OFF`
                            : `${formatPrice(parseFloat(String(product.discount_off) || '0'), storeInfo?.currenCy)} OFF`}
                        </span>
                        {' '}each item!
                      </p>
                      {totalQuantity > 0 && totalQuantity < parseInt(product.discount_qty) && (
                        <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                          Add {parseInt(product.discount_qty) - totalQuantity} more to qualify for discount
                        </p>
                      )}
                      {discountInfo?.isEligible && (
                        <div className="mt-2 p-2 bg-green-100 dark:bg-green-900/20 rounded-md">
                          <p className="text-sm font-medium text-green-700 dark:text-green-400">
                            🎉 Discount Applied! You save {formatPrice(discountInfo.discountValue, storeInfo?.currenCy)}
                          </p>
                        </div>
                      )}
                    </div>
                  )}
                </div>

                <div className="mt-8">
                  {product.variations[0].variation !== 'Nil' ? (
                    <div className="space-y-4">
                      <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                        Select Variations
                      </h3>
                      <div className="space-y-4">
                        {availableVariations.map((variation, index) => (
                          <div key={index} className="flex items-center justify-between">
                            <div>
                              <span className="text-gray-700 dark:text-gray-300">
                                {variation.variation}
                              </span>
                                <span className="ml-2 text-sm text-brand-primary dark:text-brand-light">
                                {formatPrice(
                                  (parseFloat(variation.variation_amount || '0') > 0
                                    ? parseFloat(variation.variation_amount)
                                    : parseFloat(product.sellingprice || '0')),
                                  storeInfo?.currenCy
                                )}
                              </span>
                            </div>
                            <div className="flex items-center gap-3">
                              <motion.button
                                whileTap={{ scale: 0.95 }}
                                onClick={() => handleVariationQuantityChange(variation.variation, -1)}
                                className="p-2 rounded-md bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600"
                              >
                                <Minus className="h-4 w-4" />
                              </motion.button>
                              <span className="w-8 text-center text-gray-900 dark:text-white">
                                {selectedVariations.find(v => v.variation === variation.variation)?.quantity || 0}
                              </span>
                              <motion.button
                                whileTap={{ scale: 0.95 }}
                                onClick={() => handleVariationQuantityChange(variation.variation, 1)}
                                className="p-2 rounded-md bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600"
                              >
                                <Plus className="h-4 w-4" />
                              </motion.button>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                        Quantity
                      </h3>
                      <div className="flex items-center gap-4">
                        <motion.button
                          whileTap={{ scale: 0.95 }}
                          onClick={() => setQuantity(Math.max(1, quantity - 1))}
                          className="p-2 rounded-md bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-white hover:bg-gray-200 dark:hover:bg-gray-600"
                        >
                          <Minus className="h-4 w-4" />
                        </motion.button>
                        <input
                          type="number"
                          min="1"
                          max={product.totalStock}
                          value={quantity}
                          onChange={(e) => setQuantity(Math.max(1, Math.min(product.totalStock, parseInt(e.target.value) || 1)))}
                          className="w-20 text-center rounded-md border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                        />
                        <motion.button
                          whileTap={{ scale: 0.95 }}
                          onClick={() => setQuantity(Math.min(product.totalStock, quantity + 1))}
                          className="p-2 rounded-md bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-white hover:bg-gray-200 dark:hover:bg-gray-600"
                        >
                          <Plus className="h-4 w-4" />
                        </motion.button>
                      </div>
                    </div>
                  )}
                </div>

                <motion.div
                  whileTap={{ scale: 0.98 }}
                  className="mt-8"
                >
                  <Button
                    onClick={handleAddToCart}
                    className="w-full h-12 text-lg font-medium"
                    disabled={product.variations[0].variation !== 'Nil'
                      ? availableVariations.length === 0
                      : parseInt(product.totalStock.toString()) === 0}
                  >
                    <ShoppingCart className="h-5 w-5 mr-2" />
                    {product.variations[0].variation !== 'Nil'
                      ? availableVariations.length === 0
                        ? 'Out of Stock'
                        : 'Add to Cart'
                       : parseInt(product.totalStock.toString()) === 0
                        ? 'Out of Stock'
                        : 'Add to Cart'}
                  </Button>
                </motion.div>

                <div className="mt-8 border-t dark:border-gray-700 pt-8">
                  <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
                    Description
                  </h3>
                  <div className="prose prose-sm dark:prose-invert">
                    <p className="whitespace-pre-line text-gray-700 dark:text-gray-300">
                      {product.description}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {suggestedProducts.length > 0 && (
            <div className="mt-12">
              <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-6">
                Suggested Products
              </h2>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                {suggestedProducts.map((suggestedProduct) => (
                  <div
                    key={suggestedProduct.id}
                    className="group bg-white dark:bg-gray-800 rounded-lg shadow-md overflow-hidden hover:shadow-lg transition-all duration-300"
                  >
                    <Link to={`/product/${suggestedProduct.id}`}>
                      <div className="aspect-square relative overflow-hidden">
                        <img
                          src={suggestedProduct.images[0]}
                          alt={suggestedProduct.produt_name}
                          className="w-full h-full object-cover transform group-hover:scale-110 transition-transform duration-300"
                        />
                        {suggestedProduct.postType === "1" && (
                          <div className="absolute top-2 right-2 bg-yellow-500 text-white px-2 py-1 rounded-full text-xs font-semibold">
                            Pre-Order
                          </div>
                        )}
                      </div>
                      <div className="p-4">
                        <h3 className="font-semibold text-gray-900 dark:text-white mb-2 line-clamp-2 group-hover:text-brand-primary dark:group-hover:text-brand-light transition-colors">
                          {truncateText(suggestedProduct.produt_name, 50)}
                        </h3>
                        <p className="text-sm text-gray-500 dark:text-gray-400 mb-2">
                          {suggestedProduct.category || 'Uncategorized'}
                        </p>
                        <div className="flex justify-between items-center">
                            <span className="font-bold text-gray-900 dark:text-white">
                            {formatPrice(parseFloat(suggestedProduct.sellingprice || '0'), storeInfo?.currenCy)}
                          </span>
                        </div>
                      </div>
                    </Link>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {showAddedToCartModal && <AddedToCartModal />}
      <LockNotice
        open={showLockNotice}
        onClose={() => setShowLockNotice(false)}
      />
    </>
  );
}